import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  Button,
  Typography,
  Box,
  Alert,
  Container,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import { Language as LanguageIcon } from '@mui/icons-material';
import { useTranslation, Language } from '../../i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [langAnchorEl, setLangAnchorEl] = useState<null | HTMLElement>(null);
  const { language, setLanguage, t } = useTranslation();
  
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const handleLangMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangMenuClose = () => {
    setLangAnchorEl(null);
  };

  const handleLanguageChange = (lang: Language) => {
    setLanguage(lang);
    handleLangMenuClose();
  };

  // iOS PWA fix: Force focus on touch end (after default behavior)
  const handleTouchEnd = (ref: React.RefObject<HTMLInputElement>) => {
    return () => {
      // Use setTimeout to run after the touch event completes
      setTimeout(() => {
        if (ref.current) {
          ref.current.focus();
        }
      }, 10);
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/users/sign_in`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user: {
            email,
            password
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Invalid email or password');
      }

      const data = await response.json();
      if (data.status === 200) {
        const token = response.headers.get('Authorization');
        if (token) {
          localStorage.setItem('token', token);
          if (data.data) {
            localStorage.setItem('user', JSON.stringify(data.data));
          }
          navigate('/');
        }
      } else {
        throw new Error(data.message || 'Login failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Native input styles
  const nativeInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '16px 14px',
    fontSize: '16px', // Prevents iOS zoom
    border: '1px solid #ccc',
    borderRadius: '4px',
    outline: 'none',
    fontFamily: 'Roboto, sans-serif',
    boxSizing: 'border-box',
    WebkitAppearance: 'none',
    marginBottom: '16px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    color: '#666',
    fontFamily: 'Roboto, sans-serif',
  };

  return (
    <Container maxWidth="sm">
      {/* Language Selector at top right */}
      <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
        <IconButton 
          onClick={handleLangMenuOpen} 
          color="primary"
          aria-label={t('language.select')}
        >
          <LanguageIcon />
        </IconButton>
        <Menu
          anchorEl={langAnchorEl}
          open={Boolean(langAnchorEl)}
          onClose={handleLangMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem 
            onClick={() => handleLanguageChange('en')}
            selected={language === 'en'}
          >
            {t('language.english')}
          </MenuItem>
          <MenuItem 
            onClick={() => handleLanguageChange('ta')}
            selected={language === 'ta'}
          >
            {t('language.tamil')}
          </MenuItem>
        </Menu>
      </Box>

      {/* Logo and Company Name */}
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          mt: 8,
          mb: 3 
        }}
      >
        <Box
          component="img"
          src="/apple-touch-icon.png"
          alt={t('appName')}
          sx={{
            width: 80,
            height: 80,
            objectFit: 'contain',
            mb: 2,
          }}
        />
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 600,
            color: 'primary.main',
            textAlign: 'center'
          }}
        >
          {t('appName')}
        </Typography>
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ mt: 0.5 }}
        >
          {t('managementSystem')}
        </Typography>
      </Box>

      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom>
            {t('login.title')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            {/* Using native HTML inputs for iOS PWA compatibility */}
            <div style={{ marginTop: '16px' }}>
              <label htmlFor="email" style={labelStyle}>
                {t('login.email')}
              </label>
              <input
                ref={emailRef}
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onTouchEnd={handleTouchEnd(emailRef)}
                required
                disabled={loading}
                autoComplete="email"
                inputMode="email"
                enterKeyHint="next"
                style={{
                  ...nativeInputStyle,
                  backgroundColor: loading ? '#f5f5f5' : 'white',
                }}
              />
            </div>

            <div>
              <label htmlFor="password" style={labelStyle}>
                {t('login.password')}
              </label>
              <input
                ref={passwordRef}
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onTouchEnd={handleTouchEnd(passwordRef)}
                required
                disabled={loading}
                autoComplete="current-password"
                enterKeyHint="done"
                style={{
                  ...nativeInputStyle,
                  backgroundColor: loading ? '#f5f5f5' : 'white',
                }}
              />
            </div>

            <Button 
              type="submit"
              variant="contained"
              fullWidth
              sx={{ mt: 2 }}
              disabled={loading}
            >
              {loading ? t('login.loggingIn') : t('login.loginButton')}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Link 
                to="/forgot-password" 
                style={{ 
                  color: '#1976d2', 
                  textDecoration: 'none',
                  fontSize: '14px'
                }}
              >
                {t('login.forgotPassword')}
              </Link>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
}
